// src/hooks/useData.js
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { signCategories } from "../components/Categories";
import useCanvasHistory from "./useCanvasHistory";

const DataContext = createContext(undefined);

export const DataProvider = ({ children }) => {
  const [canvas, setCanvas] = useState(null);
  const [signCategoryState, setSignCategoryState] = useState({
    name: "",
    productCode: "",
  });
  const [signCategorySizeState, setSignCategorySizeState] = useState({
    width: 24,
    height: 24,
  });
  const [canvasScale, setCanvasScale] = useState(1);
  const [selectedObject, setSelectedObject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noticesMessage, setNoticesMessage] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [basketItem, setBasketItem] = useState({
    productCode: "",
    width: 24,
    height: 24,
    gauges: "",
    addOns: [],
  });
  const [attributes, setAttributes] = useState({});
  const [attributeOptions, setAttributeOptions] = useState({});
  const [attributeValues, setAttributeValues] = useState({});
  const [upsaleSignPrice, setUpsaleSignPrice] = useState("0.00");
  const [selectedTool, setSelectedTool] = useState("category");
  const [contextualText, setContextualText] = useState("");
  const [fontSize, setFontSize] = useState(2);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [showRuler, setShowRuler] = useState(true);
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [projectName, setProjectName] = useState("");
  const [mivaGlobals, setMivaGlobals] = useState(null);

  // Initialize useCanvasHistory
  const fabricCanvasRef = useRef(null);
  const { saveHistory } = useCanvasHistory(fabricCanvasRef);

  // Sync fabricCanvasRef with canvas
  useEffect(() => {
    fabricCanvasRef.current = canvas;
  }, [canvas]);

  // Extract mivaGlobals from the DOM on mount
  useEffect(() => {
    const mivaDiv = document.getElementById("mivaglobals");
    if (!mivaDiv) {
      console.warn("useData: #mivaglobals element not found in DOM");
      setMivaGlobals(null);
      return;
    }

    const dataset = mivaDiv.dataset;
    const globals = {
      basket_id: dataset.basketid || null,
      customer_id: dataset.customerid || null,
      sign_type_id: dataset.signtypeid || null,
      sizes_name: dataset.sizesname || null,
      product_code: dataset.productcode || null,
      managed_template_id: dataset.managedtemplateid || null,
      has_file_upload: dataset.hasfileupload === "1" ? true : false,
      csrf_token: dataset.csrftoken || null,
      cu_sess_id: dataset.cusessid || null,
      session_id: dataset.sessionid || null,
    };

    console.log("useData: Extracted mivaGlobals", globals);
    setMivaGlobals(globals);
  }, []);

  // Set signCategoryState.name based on mivaGlobals.sign_type_id
  useEffect(() => {
    if (mivaGlobals && mivaGlobals.sign_type_id) {
      const signTypeId = mivaGlobals.sign_type_id;
      console.log("useData: Checking mivaGlobals.sign_type_id", { signTypeId });

      // Verify sign_type_id matches a category
      const category = signCategories.find(
        (cat) => cat.name === signTypeId || cat.productCode === signTypeId
      );
      if (category) {
        setSignCategoryState({
          name: category.name,
          productCode: category.productCode,
        });
        console.log("useData: Set signCategoryState", {
          name: category.name,
          productCode: category.productCode,
        });
      } else {
        console.warn("useData: Invalid sign_type_id, no matching category", {
          signTypeId,
        });
        setSnackbar({
          open: true,
          message: `Invalid sign type: ${signTypeId}`,
          severity: "warning",
        });
      }
    } else if (mivaGlobals && !mivaGlobals.sign_type_id) {
      console.log("useData: No sign_type_id in mivaGlobals");
    }
  }, [mivaGlobals, setSnackbar]);

  const setCanvasWithLog = useCallback((value) => {
    console.log("useData: Setting canvas", { canvas: !!value });
    setCanvas(value);
  }, []);

  const setSignCategorySizeStateWithLog = useCallback((size) => {
    console.log("useData: Setting signCategorySizeState", { size });
    setSignCategorySizeState(size);
  }, []);

  const stableSignCategorySizeState = useMemo(
    () => ({
      width: signCategorySizeState.width || 24,
      height: signCategorySizeState.height || 24,
    }),
    [signCategorySizeState]
  );

  // Split context to reduce re-renders
  const canvasContext = useMemo(
    () => ({
      canvas,
      setCanvas: setCanvasWithLog,
      saveHistory,
      backgroundColor,
      setBackgroundColor,
    }),
    [canvas, setCanvasWithLog, saveHistory, backgroundColor]
  );

  const signCategoryContext = useMemo(
    () => ({
      signCategories,
      signCategoryState,
      setSignCategoryState,
      signCategorySizeState: stableSignCategorySizeState,
      setSignCategorySizeState: setSignCategorySizeStateWithLog,
    }),
    [
      signCategoryState,
      stableSignCategorySizeState,
      setSignCategorySizeStateWithLog,
    ]
  );

  const uiContext = useMemo(
    () => ({
      canvasScale,
      setCanvasScale,
      selectedObject,
      setSelectedObject,
      loading,
      setLoading,
      noticesMessage,
      setNoticesMessage,
      snackbar,
      setSnackbar,
      showRuler,
      setShowRuler,
      selectedTool,
      setSelectedTool,
      contextualText,
      setContextualText,
      fontSize,
      setFontSize,
      projectName,
      setProjectName,
    }),
    [
      canvasScale,
      selectedObject,
      loading,
      noticesMessage,
      snackbar,
      showRuler,
      selectedTool,
      contextualText,
      fontSize,
      projectName,
      setProjectName,
    ]
  );

  const productContext = useMemo(
    () => ({
      basketItem,
      setBasketItem,
      attributes,
      setAttributes,
      attributeOptions,
      setAttributeOptions,
      attributeValues,
      setAttributeValues,
      upsaleSignPrice,
      setUpsaleSignPrice,
      mivaGlobals,
    }),
    [
      basketItem,
      attributes,
      attributeOptions,
      attributeValues,
      upsaleSignPrice,
      mivaGlobals,
    ]
  );

  const userContext = useMemo(
    () => ({
      user,
      setUser,
      error,
      setError,
    }),
    [user, error]
  );

  const value = useMemo(
    () => ({
      ...canvasContext,
      ...signCategoryContext,
      ...uiContext,
      ...productContext,
      ...userContext,
    }),
    [canvasContext, signCategoryContext, uiContext, productContext, userContext]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export default DataProvider;
